import {
  cn,
  twVariants
} from "./chunk-LJ5F4WFH.js";

// src/Button/ButtonBase.ts
var baseButtonStyles = twVariants({
  slots: {
    root: [
      "inline-flex items-center justify-center font-bold outline-none transition-all",
      "active:scale-95",
      "disabled:scale-100 disabled:cursor-not-allowed disabled:select-none",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-radiant-blue focus-visible:ring-offset-2"
    ]
  },
  variants: {
    color: {
      macro: "",
      brand: "",
      danger: "",
      warning: "",
      success: "",
      information: "",
      neutral: ""
    },
    variant: {
      primary: "",
      "primary-inverse": "",
      secondary: { root: "border" },
      "secondary-inverse": { root: "border" },
      tonal: "",
      ghost: ""
    }
  },
  compoundVariants: [
    {
      color: "macro",
      variant: "primary",
      className: {
        root: [
          "bg-macro-primary-enabled text-neutral-inversed",
          "hover:bg-macro-primary-hovered",
          "active:bg-macro-primary-hovered",
          "disabled:bg-macro-primary-disabled"
        ]
      }
    },
    {
      color: "brand",
      variant: "primary",
      className: {
        root: [
          "bg-brand-primary-enabled text-neutral-inversed",
          "hover:bg-brand-primary-hovered",
          "active:bg-brand-primary-hovered",
          "disabled:bg-brand-primary-disabled"
        ]
      }
    },
    {
      color: "brand",
      variant: "primary-inverse",
      className: {
        root: [
          "bg-brand-inversed-primary-enabled text-brand-inversed-primary-enabled",
          "hover:bg-brand-inversed-primary-hovered",
          "active:bg-brand-inversed-primary-hovered",
          "disabled:bg-brand-inversed-primary-enabled disabled:text-brand-inversed-primary-disabled"
        ]
      }
    },
    {
      color: "danger",
      variant: "primary",
      className: {
        root: [
          "bg-danger-primary-enabled text-neutral-inversed",
          "hover:bg-danger-primary-hovered",
          "active:bg-danger-primary-hovered",
          "disabled:bg-danger-primary-disabled"
        ]
      }
    },
    {
      color: "brand",
      variant: "secondary",
      className: {
        root: [
          "border-brand-secondary-enabled bg-transparent text-brand-secondary-enabled",
          "disabled:border-brand-secondary-disabled disabled:bg-transparent disabled:text-brand-secondary-disabled",
          "hover:bg-brand-secondary-hovered",
          "active:bg-brand-secondary-hovered"
        ]
      }
    },
    {
      color: "brand",
      variant: "secondary-inverse",
      className: {
        root: [
          "border-neutral-action-inversed bg-transparent text-neutral-inversed",
          "disabled:border-brand-secondary-disabled disabled:text-brand-inversed-primary-disabled disabled:hover:bg-transparent",
          "hover:bg-brand-secondary-hovered-inverse hover:text-brand-secondary-enabled",
          "active:bg-brand-secondary-hovered-inverse active:text-brand-secondary-enabled"
        ]
      }
    },
    {
      color: "danger",
      variant: "secondary",
      className: {
        root: [
          "border-alert-danger bg-transparent text-danger-alert-text",
          "disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-danger-alert-text",
          "hover:bg-danger-secondary-hovered",
          "active:bg-danger-secondary-hovered"
        ]
      }
    },
    {
      color: "warning",
      variant: "secondary",
      className: {
        root: [
          "border-alert-warning bg-transparent text-warning-alert-text",
          "disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-warning-alert-text",
          "hover:bg-warning-secondary-hovered",
          "active:bg-warning-secondary-hovered"
        ]
      }
    },
    {
      color: "success",
      variant: "secondary",
      className: {
        root: [
          "border-alert-success bg-transparent text-success-alert-text",
          "disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-success-alert-text",
          "hover:bg-success-secondary-hovered",
          "active:bg-success-secondary-hovered"
        ]
      }
    },
    {
      color: "information",
      variant: "secondary",
      className: {
        root: [
          "border-alert-information bg-transparent text-information-alert-text",
          "disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-information-alert-text",
          "hover:bg-information-secondary-hovered",
          "active:bg-information-secondary-hovered"
        ]
      }
    },
    {
      color: "brand",
      variant: "tonal",
      className: {
        root: [
          "bg-brand-tonal-enabled text-neutral-primary",
          "disabled:bg-surface-light disabled:text-neutral-ghost-disabled",
          "hover:bg-brand-tonal-hovered",
          "active:bg-brand-tonal-hovered"
        ]
      }
    },
    {
      color: "neutral",
      variant: "ghost",
      className: {
        root: [
          "bg-transparent text-neutral-ghost-enabled",
          "disabled:bg-transparent disabled:text-neutral-ghost-disabled",
          "hover:bg-neutral-ghost-hovered",
          "active:bg-neutral-ghost-hovered"
        ]
      }
    },
    {
      color: "brand",
      variant: "ghost",
      className: {
        root: [
          "bg-transparent text-brand-ghost-enabled",
          "disabled:bg-transparent disabled:text-brand-ghost-disabled",
          "hover:bg-brand-ghost-hovered",
          "active:bg-brand-ghost-hovered"
        ]
      }
    },
    {
      color: "danger",
      variant: "ghost",
      className: {
        root: [
          "bg-transparent text-danger-ghost-enabled",
          "disabled:bg-transparent disabled:text-danger-ghost-disabled",
          "hover:bg-danger-ghost-hovered",
          "active:bg-danger-ghost-hovered"
        ]
      }
    }
  ]
});

// src/Button/Button.tsx
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
var buttonStyles = twVariants({
  extend: baseButtonStyles,
  slots: {
    root: [
      "inline-flex w-auto items-center justify-center whitespace-nowrap rounded-full font-semibold outline-none transition-all"
    ]
  },
  variants: {
    size: {
      sm: {
        root: "h-8 px-4 text-body-2 [&_svg]:h-4 [&_svg]:w-4"
      },
      md: {
        root: "h-10 px-5 [&_svg]:h-5 [&_svg]:w-5"
      },
      lg: {
        root: "h-13 px-6 [&_svg]:h-6 [&_svg]:w-6"
      }
    }
  }
});
var Button = React.forwardRef(
  ({
    className,
    variant = "primary",
    color,
    size = "md",
    asChild = false,
    type = "button",
    children,
    ...props
  }, ref) => {
    const Comp = asChild ? Slot : "button";
    const defaultColor = variant === "ghost" ? "neutral" : "brand";
    const { root } = buttonStyles({
      variant,
      size,
      color: color ?? defaultColor
    });
    return /* @__PURE__ */ React.createElement(Comp, { className: cn(root(), className), ref, type, ...props }, children);
  }
);
Button.displayName = "Button";

export {
  baseButtonStyles,
  buttonStyles,
  Button
};
//# sourceMappingURL=chunk-JSCFMZUG.js.map